import { notification } from 'antd';
import {
  autocomplete,
  igjToezichtSearch,
  fetchIgjToezichtFilters,
} from '@/pages/igj_toezicht/list/service';
import { TableListData, AutoCompleteParams, SearchParams } from '@/pages/igj_toezicht/list/data';

export type IgjDisplayLang = 'nl' | 'en';

const IGJ_DISPLAY_LANG_KEY = 'igj_toezicht_display_lang';

function readStoredDisplayLang(): IgjDisplayLang {
  if (typeof window === 'undefined') return 'nl';
  return window.localStorage.getItem(IGJ_DISPLAY_LANG_KEY) === 'en' ? 'en' : 'nl';
}

export interface IgjToezichtTableListStateType {
  data: TableListData;
  isFiltering: boolean;
  autoCompleteData: AutoCompleteParams;
  searchData: SearchParams;
  displayLang: IgjDisplayLang;
  filtersMeta: {
    loaded: boolean;
    supervision_document_types: string[];
    health_care_sectors: string[];
  };
}

let residualSearch: any;

function normalizeFilterArray(input: any): any[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((f: any) => {
      if (!f || typeof f !== 'object') return null;
      if (
        typeof f.search_filter_field === 'string' &&
        typeof f.search_operation === 'string' &&
        f.search_filter_field_value !== undefined
      ) {
        return f;
      }
      if (
        typeof f.field === 'string' &&
        typeof f.operation === 'string' &&
        f.value !== undefined
      ) {
        return {
          search_filter_field: f.field,
          search_operation: f.operation,
          search_filter_field_value: f.value,
        };
      }
      return null;
    })
    .filter(Boolean);
}

function isPaginationOnlyPayload(payload: any): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const hasPaginationKeys =
    payload.currentPage !== undefined ||
    payload.current !== undefined ||
    payload.pageSize !== undefined ||
    payload.start !== undefined ||
    payload.length !== undefined;
  const hasSearchOrFilter = payload.search !== undefined || payload.filter !== undefined;
  return hasPaginationKeys && !hasSearchOrFilter;
}

export default {
  namespace: 'igjToezichtTableList',

  state: {
    data: {
      list: [],
      pagination: {},
    },
    isFiltering: false,
    autoCompleteData: {
      search: '',
      dataSource: [],
    },
    searchData: {
      search: {
        none: '',
      },
      filter: [],
      pagination: {},
      searchField: false,
    },
    filtersMeta: {
      loaded: false,
      supervision_document_types: [],
      health_care_sectors: [],
    },
    displayLang: readStoredDisplayLang(),
  },

  effects: {
    *fetch({ payload }, { call, put, select }) {
      let response;
      const displayLang: IgjDisplayLang =
        yield select((s: { igjToezichtTableList: IgjToezichtTableListStateType }) =>
          s.igjToezichtTableList?.displayLang === 'en' ? 'en' : 'nl',
        );
      const withLang = { ...payload, display_lang: displayLang };
      if (withLang.filter !== undefined) {
        withLang.filter = normalizeFilterArray(withLang.filter);
      }

      if (isPaginationOnlyPayload(withLang) && residualSearch) {
        const nextPayload = {
          ...residualSearch,
          pagination: payload,
          display_lang: displayLang,
        };
        response = yield call(igjToezichtSearch, nextPayload);
      } else if (withLang.searchField || withLang.filterField) {
        if (withLang.pagination) {
          residualSearch = withLang;
          response = yield call(igjToezichtSearch, withLang);
        } else {
          residualSearch.pagination = payload;
          response = yield call(igjToezichtSearch, {
            ...residualSearch,
            display_lang: displayLang,
          });
        }
      } else {
        const fetchObj = {
          search: { none: '' },
          filter: [],
          searchField: true,
          filterField: true,
          pagination: payload,
          display_lang: displayLang,
        };
        response = yield call(igjToezichtSearch, fetchObj);
      }

      if (response && response.status === 200) {
        yield put({
          type: 'save',
          payload: response,
        });
      } else {
        notification.error({
          message: 'Error while fetching IGJ toezicht documents',
        });
      }
    },

    *fetchFilters(_, { call, put }) {
      const response = yield call(fetchIgjToezichtFilters);
      const payload = response.value ? response.value : response;

      yield put({
        type: 'saveFilters',
        payload,
      });
    },

    *refetchWithCurrentQuery(_, { put }) {
      if (residualSearch !== undefined) {
        yield put({ type: 'fetch', payload: residualSearch });
      }
    },

    *autoComplete({ payload }, { call, put, select }) {
      const displayLang: IgjDisplayLang =
        yield select((s: { igjToezichtTableList: IgjToezichtTableListStateType }) =>
          s.igjToezichtTableList?.displayLang === 'en' ? 'en' : 'nl',
        );
      const response = yield call(autocomplete, { ...payload, display_lang: displayLang });
      if (response) {
        yield put({
          type: 'saveEntries',
          payload: response,
        });
      }
    },

    *clearResidual(_, { put }) {
      if (residualSearch !== undefined) {
        residualSearch.search.none = '';
        residualSearch.filter = [];
      }
      yield put({
        type: 'clearedResidual',
      });
    },

    *toggleFilter(_, { put }) {
      yield put({
        type: 'toggleIsFiltering',
      });
    },

    *setFilter({ payload }, { put }) {
      yield put({
        type: 'setIsFiltering',
        payload,
      });
    },
  },

  reducers: {
    clearedResidual(state) {
      return {
        ...state!,
      };
    },

    save(state, action) {
      const payload = action.payload || {};
      const pag = payload.pagination || {};
      const data = {
        ...payload,
        pagination: {
          ...pag,
          current: pag.currentPage ?? pag.current,
        },
      };
      return {
        ...state!,
        data,
        autoCompleteData: { ...state!.autoCompleteData, dataSource: [] },
      };
    },

    saveEntries(state, action) {
      return {
        ...state!,
        autoCompleteData: { ...state!.autoCompleteData, dataSource: action.payload },
      };
    },

    toggleIsFiltering(state) {
      return {
        ...state!,
        isFiltering: !state!.isFiltering,
      };
    },

    setIsFiltering(state, action) {
      return {
        ...state!,
        isFiltering: Boolean(action.payload),
      };
    },

    addLoading(state, action) {
      const idToUpdate = action.payload.submission_id || action.payload.dbid;
      const dataFinal = state?.data;

      if (dataFinal) {
        const updatedList = dataFinal.list.map(item => {
          if (item.dbid === idToUpdate) {
            return { ...item, loading: true };
          }
          return item;
        });

        return {
          ...state!,
          data: {
            ...dataFinal,
            list: updatedList,
          },
        };
      }

      return state!;
    },

    removeLoading(state, action) {
      const idToUpdate = action.payload.submission_id || action.payload.dbid;
      const dataFinal = state?.data;

      if (dataFinal) {
        const updatedList = dataFinal.list.map(item => {
          if (item.dbid === idToUpdate) {
            return { ...item, loading: false };
          }
          return item;
        });

        return {
          ...state!,
          data: {
            ...dataFinal,
            list: updatedList,
          },
        };
      }

      return state!;
    },

    saveFilters(state, action) {
      const p = action.payload || {};
      return {
        ...state!,
        filtersMeta: {
          loaded: true,
          supervision_document_types: Array.isArray(p.supervision_document_types)
            ? p.supervision_document_types
            : [],
          health_care_sectors: Array.isArray(p.health_care_sectors) ? p.health_care_sectors : [],
        },
      };
    },

    setDisplayLang(state, action) {
      const v: IgjDisplayLang = action.payload === 'en' ? 'en' : 'nl';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(IGJ_DISPLAY_LANG_KEY, v);
      }
      return {
        ...state!,
        displayLang: v,
      };
    },
  },
};
